import { internalFetch } from './fetchHelper';
import { getBackendApiUrl, getBackendApiKey } from './runtimeEnv';
import {
  parseNotificationPreferencesFromOtherInfo,
  type NotificationPreferencesPayload,
} from './notificationPreferences';

export interface UserPreferenceResponse {
  userId: number;
  preference?: Record<string, unknown>;
}

export async function fetchUserPreference(
  abortController: AbortController
): Promise<UserPreferenceResponse> {
  return internalFetch(preferenceUrl(), 'GET', abortController);
}

export async function fetchNotificationPreferences(
  abortController: AbortController
): Promise<NotificationPreferencesPayload> {
  const response = await fetchUserPreference(abortController);
  return parseNotificationPreferencesFromOtherInfo({
    preference: response.preference,
  });
}

export type ColorScheme = 'light' | 'dark';

export type ColorSchemePreference = ColorScheme | 'system';

export const COLOR_SCHEME_PREFERENCE_KEY = 'colorScheme';

const isColorSchemePreference = (
  value: unknown
): value is ColorSchemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

export const parseColorScheme = (
  preference?: Record<string, unknown> | null
): ColorSchemePreference => {
  const value = preference?.[COLOR_SCHEME_PREFERENCE_KEY];
  return isColorSchemePreference(value) ? value : 'light';
};

export const resolveColorScheme = (
  preference: ColorSchemePreference
): ColorScheme => {
  if (preference !== 'system') return preference;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

const preferenceUrl = () =>
  `${getBackendApiUrl()}/a/me/preference?code=${getBackendApiKey()}`;

export async function updateUserPreference(
  abortController: AbortController,
  patch: Record<string, unknown>
): Promise<void> {
  const current = await fetchUserPreference(abortController);
  const preference = {
    ...(current.preference ?? {}),
    ...patch,
  };
  await internalFetch(
    preferenceUrl(),
    'PUT',
    abortController,
    JSON.stringify({ preference }),
    true
  );
}

export async function updateNotificationPreferences(
  abortController: AbortController,
  preferences: NotificationPreferencesPayload
): Promise<void> {
  await updateUserPreference(abortController, preferences);
}

export async function updateColorScheme(
  abortController: AbortController,
  colorScheme: ColorSchemePreference
): Promise<void> {
  await updateUserPreference(abortController, {
    [COLOR_SCHEME_PREFERENCE_KEY]: colorScheme,
  });
}
